/**
 * Strategy Constants
 *
 * Centralized constants and default values for strategy creation.
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import {
  SelectOption,
  StrategyConfig,
  Leg,
} from "../../types/strategy";

import {
  UNDERLYING, SELECTION_METHOD, PREMIUM_OPERATOR, EXPIRY_TYPE_CONST,
  RISK_MANAGEMENT_TYPE, RE_ENTRY_TYPE, RE_EXECUTE_TYPE, MTM_TYPE, PRODUCT_TYPE,
  TRADING_TYPE, INTRADAY_EXIT_MODE, OPTION_TYPE, ACTION_TYPE, WEEKDAYS,
  MARKET_HOURS,
} from "../../constants/tradingConstants";

// Index options
export const INDEX_OPTIONS: SelectOption[] = [
  { value: UNDERLYING.NIFTY, label: "NIFTY" },
  { value: UNDERLYING.BANKNIFTY, label: "BANKNIFTY" },
  { value: UNDERLYING.FINNIFTY, label: "FINNIFTY" },
  { value: UNDERLYING.MIDCPNIFTY, label: "MIDCPNIFTY" },
  { value: UNDERLYING.SENSEX, label: "SENSEX" },
  { value: UNDERLYING.BANKEX, label: "BANKEX" },
];

// Selection method options
export const SELECTION_METHOD_OPTIONS: SelectOption[] = [
  { value: SELECTION_METHOD.ATM_POINTS, label: "ATM Points" },
  { value: SELECTION_METHOD.ATM_PERCENT, label: "ATM Percent" },
  { value: SELECTION_METHOD.PREMIUM, label: "Premium" },
  {
    value: SELECTION_METHOD.PERCENTAGE_OF_STRADDLE_PREMIUM,
    label: "CP as SP",
    tooltip:
      "Selects the strike whose premium is closest to the chosen percent of straddle premium at entry time. CP = Closest Premium, SP = Straddle Premium.",
  },
  { value: SELECTION_METHOD.STRADDLE_WIDTH, label: "Straddle Width" },
  {
    value: SELECTION_METHOD.PERCENTAGE_OF_UNDERLYING,
    label: "% of Underlying",
    tooltip:
      "Selects the strike whose premium is closest to the chosen percent of the underlying spot price at entry time.",
  },
];

// Straddle width multiplier options: signed multiplier of ATM straddle premium,
// range [-10, +10] in steps of 0.25 (81 options).
// Top of list = most negative offset ("ATM-10*SP"), bottom = most positive ("ATM+10*SP").
// Format must round-trip with Number.toString() — so 0 → "0" (NOT "0.0"), 0.5 → "0.5",
// 10 → "10" (NOT "10.0"). Native <select> compares value strings exactly.
const formatStraddleWidthValue = (n: number): string =>
  parseFloat(n.toFixed(2)).toString();
const buildStraddleWidthOptions = (): SelectOption[] => {
  const opts: SelectOption[] = [];
  for (let i = -40; i <= 40; i++) {
    const v = i * 0.25;
    const value = formatStraddleWidthValue(v);
    let label: string;
    if (v === 0) label = "ATM";
    else if (v < 0) label = `ATM-${formatStraddleWidthValue(Math.abs(v))}*SP`;
    else label = `ATM+${formatStraddleWidthValue(v)}*SP`;
    opts.push({ value, label });
  }
  return opts;
};
export const STRADDLE_WIDTH_OPTIONS: SelectOption[] = buildStraddleWidthOptions();

// Premium operator options
export const PREMIUM_OPERATOR_OPTIONS: SelectOption[] = [
  { value: PREMIUM_OPERATOR.CLOSEST, label: "Closest ~" },
  { value: PREMIUM_OPERATOR.GTE, label: "GTE >=" },
  { value: PREMIUM_OPERATOR.LTE, label: "LTE <=" },
];

// PERCENTAGE_OF_UNDERLYING limits — single source of truth for the range,
// default, and decimal-precision rule. Mirrored by backend validation in
// lambda_functions/strategy/strategy_manager.py (`validate_and_enhance_legs`).
// Changing any of these means updating the backend too.
//
// The HTML5 `step` attribute on the LegMatrix input is INTENTIONALLY 0.01 (not
// a value derived from this map): HTML5 stepMismatch flags values like 0.06 /
// 0.07 / 0.13 as :invalid when step=0.05, but the actual validator accepts any
// ≤DECIMALS-decimal value. Keeping step=0.01 aligns the browser's :invalid
// pseudo-class with the real validation contract.
export const UNDERLYING_PCT = {
  MIN: 0.05,
  MAX: 10,
  DEFAULT: 1,
  DECIMALS: 2,
} as const;

// Expiry type constants (re-exported for backward compatibility)
export const EXPIRY_TYPE = {
  WEEKLY: EXPIRY_TYPE_CONST.WEEKLY,
  MONTHLY: EXPIRY_TYPE_CONST.MONTHLY,
  NEXT_WEEKLY: EXPIRY_TYPE_CONST.NEXT_WEEKLY,
  NEXT_MONTHLY: EXPIRY_TYPE_CONST.NEXT_MONTHLY,
} as const;

// Expiry type options
export const EXPIRY_TYPE_OPTIONS: SelectOption[] = [
  { value: EXPIRY_TYPE_CONST.WEEKLY, label: "Weekly" },
  { value: EXPIRY_TYPE_CONST.NEXT_WEEKLY, label: "Next Weekly" },
  { value: EXPIRY_TYPE_CONST.MONTHLY, label: "Monthly" },
  { value: EXPIRY_TYPE_CONST.NEXT_MONTHLY, label: "Next Monthly" },
];

// Helper: check whether an expiry_type refers to a "next" contract.
export const isNextExpiry = (t: string | null | undefined): boolean =>
  t === EXPIRY_TYPE_CONST.NEXT_WEEKLY || t === EXPIRY_TYPE_CONST.NEXT_MONTHLY;

// Risk management type options
export const RISK_MANAGEMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: RISK_MANAGEMENT_TYPE.POINTS, label: "Points" },
  { value: RISK_MANAGEMENT_TYPE.PERCENTAGE, label: "Percentage" },
  { value: RISK_MANAGEMENT_TYPE.RANGE, label: "Range" },
];

// Target profit type options (without RANGE)
export const TARGET_PROFIT_TYPE_OPTIONS: SelectOption[] = [
  { value: RISK_MANAGEMENT_TYPE.POINTS, label: "Points" },
  { value: RISK_MANAGEMENT_TYPE.PERCENTAGE, label: "Percentage" },
];

// Re-entry type options
export const RE_ENTRY_TYPE_OPTIONS: SelectOption[] = [
  { value: RE_ENTRY_TYPE.SL_REENTRY, label: "SL ReEntry" },
  { value: RE_ENTRY_TYPE.SL_RECOST, label: "SL ReCost" },
  { value: RE_ENTRY_TYPE.SL_REEXEC, label: "SL ReExec" },
];

// Re-execute type options
export const RE_EXECUTE_TYPE_OPTIONS: SelectOption[] = [
  { value: RE_EXECUTE_TYPE.TP_REEXEC, label: "TP ReExec" },
];

// Count options (1-5)
export const COUNT_OPTIONS: SelectOption[] = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

// MTM type options
export const MTM_TYPE_OPTIONS: SelectOption[] = [
  { value: MTM_TYPE.TOTAL_MTM, label: "Total MTM" },
  { value: MTM_TYPE.COMBINED_PREMIUM_PERCENT, label: "Combined Premium %" },
];

// Product type options
export const PRODUCT_TYPE_OPTIONS: SelectOption[] = [
  { value: PRODUCT_TYPE.MIS, label: "MIS (Intraday)" },
  { value: PRODUCT_TYPE.NRML, label: "NRML (Carry Forward)" },
];

// Time options (24 hours)
export const HOUR_OPTIONS: SelectOption[] = Array.from(
  { length: 24 },
  (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: i.toString().padStart(2, "0"),
  })
);

// Minute options (60 minutes)
export const MINUTE_OPTIONS: SelectOption[] = Array.from(
  { length: 60 },
  (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: i.toString().padStart(2, "0"),
  })
);

// Trading hours: platform managed window (MARKET_HOURS.SESSION_START ..
// SESSION_LAST). Entry from 09:16, exit till 15:39 (one min before the 15:40
// close). Single source of truth: MARKET_HOURS in tradingConstants.ts.
const SESSION_START_MIN =
  MARKET_HOURS.SESSION_START_HOUR * 60 + MARKET_HOURS.SESSION_START_MINUTE;
const SESSION_LAST_MIN =
  MARKET_HOURS.SESSION_LAST_HOUR * 60 + MARKET_HOURS.SESSION_LAST_MINUTE;
const pad2 = (n: number): string => n.toString().padStart(2, "0");

export const MARKET_HOUR_OPTIONS: SelectOption[] = HOUR_OPTIONS.filter(
  (o) =>
    parseInt(o.value) >= MARKET_HOURS.SESSION_START_HOUR &&
    parseInt(o.value) <= MARKET_HOURS.SESSION_LAST_HOUR
);

export const getMarketMinuteOptions = (hour: string): SelectOption[] => {
  const h = parseInt(hour);
  if (h === MARKET_HOURS.SESSION_START_HOUR)
    return MINUTE_OPTIONS.filter(
      (o) => parseInt(o.value) >= MARKET_HOURS.SESSION_START_MINUTE
    );
  if (h === MARKET_HOURS.SESSION_LAST_HOUR)
    return MINUTE_OPTIONS.filter(
      (o) => parseInt(o.value) <= MARKET_HOURS.SESSION_LAST_MINUTE
    );
  return MINUTE_OPTIONS;
};

export const clampToMarketTime = (
  hour: string,
  minute: string
): { hour: string; minute: string } => {
  const h = parseInt(hour);
  const m = parseInt(minute);
  const totalMin = h * 60 + m;
  if (totalMin < SESSION_START_MIN)
    return {
      hour: pad2(MARKET_HOURS.SESSION_START_HOUR),
      minute: pad2(MARKET_HOURS.SESSION_START_MINUTE),
    };
  if (totalMin > SESSION_LAST_MIN)
    return {
      hour: pad2(MARKET_HOURS.SESSION_LAST_HOUR),
      minute: pad2(MARKET_HOURS.SESSION_LAST_MINUTE),
    };
  return { hour, minute };
};

// Default strategy configuration
export const DEFAULT_STRATEGY_CONFIG: StrategyConfig = {
  entryTimeHour: pad2(MARKET_HOURS.SESSION_START_HOUR),
  entryTimeMinute: pad2(MARKET_HOURS.SESSION_START_MINUTE),
  exitTimeHour: pad2(MARKET_HOURS.SESSION_LAST_HOUR),
  exitTimeMinute: pad2(MARKET_HOURS.SESSION_LAST_MINUTE),
  rangeBreakout: false,
  rangeBreakoutTimeHour: "09",
  rangeBreakoutTimeMinute: "30",
  orbContinueOvernight: false,
  moveSlToCost: false,
  expiryType: EXPIRY_TYPE_CONST.WEEKLY,
  productType: PRODUCT_TYPE.NRML,
  tradingType: TRADING_TYPE.INTRADAY,
  intradayExitMode: INTRADAY_EXIT_MODE.SAME_DAY,
  expiryDayRollover: false,
  entryDays: WEEKDAYS,
  exitDays: WEEKDAYS,
  entryTradingDaysBeforeExpiry: 4, // Weekly default
  exitTradingDaysBeforeExpiry: 0, // Default for both weekly and monthly
  targetProfit: {
    type: MTM_TYPE.TOTAL_MTM,
    value: 0,
  },
  mtmStopLoss: {
    type: MTM_TYPE.TOTAL_MTM,
    value: 0,
  },
};

// Default position template (without ID)
export const createDefaultPositionTemplate = (): Omit<Leg, "id"> => ({
  optionType: OPTION_TYPE.CE,
  actionType: ACTION_TYPE.BUY,
  strikePrice: "ATM",
  totalLots: 1,
  selectionMethod: SELECTION_METHOD.ATM_POINTS,

  // Default premium selection values
  premiumOperator: PREMIUM_OPERATOR.CLOSEST,
  premiumValue: 0,

  // Default straddle premium values
  straddlePremiumOperator: PREMIUM_OPERATOR.CLOSEST,
  straddlePremiumPercentage: 5,

  // Default straddle width multiplier (signed, range [-10, 10], step 0.25)
  straddleWidth: 0,

  // Default PERCENTAGE_OF_UNDERLYING values (range/decimals in UNDERLYING_PCT)
  underlyingPremiumOperator: PREMIUM_OPERATOR.CLOSEST,
  underlyingPercentage: UNDERLYING_PCT.DEFAULT,

  // Default risk management values
  stopLoss: {
    enabled: false,
    type: RISK_MANAGEMENT_TYPE.PERCENTAGE,
    value: 0,
  },
  targetProfit: {
    enabled: false,
    type: RISK_MANAGEMENT_TYPE.PERCENTAGE,
    value: 0,
  },
  trailingStopLoss: {
    enabled: false,
    type: RISK_MANAGEMENT_TYPE.POINTS,
    instrumentMoveValue: 0,
    stopLossMoveValue: 0,
  },
  waitAndTrade: {
    enabled: false,
    type: RISK_MANAGEMENT_TYPE.PERCENTAGE,
    value: 0,
    continueOvernight: false,
    // Phase 6 review-fix M3 — match transformToFrontend's 5-key seed so fresh
    // legs (via createNewPosition) and backend-loaded legs fingerprint identically.
    tradeOnlyFirstEntry: false,
  },
  reEntry: {
    enabled: false,
    type: RE_ENTRY_TYPE.SL_REENTRY,
    count: 1,
  },
  reExecute: {
    enabled: false,
    type: RE_EXECUTE_TYPE.TP_REEXEC,
    count: 1,
  },
});

// Default positional values
export const DEFAULT_POSITIONAL_VALUES = {
  entryDays: 4,
  exitDays: 0,
};

// Slider range limits
export const SLIDER_RANGE = {
  WEEKLY_MAX: 4,
  MONTHLY_MAX: 24,
};
